import _ from 'lodash';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styles from './IntroScreen.module.scss';
import Button from './Button';
import posed, {PoseGroup} from 'react-pose';
import {easing} from 'popmotion';
import gameConstants from './gameConstants';
import classNames from 'classnames';
import StepDescription from './intro/StepDescription';
import Step3Score from './intro/Step3Score';
import Step3Nimbus from './intro/Step3Nimbus';
import Step4CloudText from './intro/Step4CloudText';
import {createGame} from './gameHelpers';
import audioAnalyser from './audioAnalyser';
import SoundWave from './game/SoundWave';
import makeIntroSoundWaveProvider from './introSoundWaveProvider';
import * as audioBundle from './audioBundle';

const Section = posed.section({
    enter: {
        rotate: 0,
        transition: props => ({
            type: 'keyframes',
            times: [0, 1],
            values: props.startedOnce ? [-90, 0] : [0, 0],
            easings: [easing.easeIn],
            duration: 650,
        }),
    },
    exit: {
        rotate: 0,
    },
});

const combine = (animation1, animation2) => ({
    enter: {
        ...animation1.enter,
        ...animation2.enter,
        transition: props => ({
            ...resolveTransition(animation1.enter.transition, props),
            ...resolveTransition(animation2.enter.transition, props),
        }),
    },
    exit: {
        ...animation1.exit,
        ...animation2.exit,
        transition: props => ({
            ...resolveTransition(animation1.exit.transition, props),
            ...resolveTransition(animation2.exit.transition, props),
        }),
    },
});

function resolveTransition(transition, props) {
    if (_.isFunction(transition)) {
        return transition(props);
    }

    return transition;
}

const appearFromDownCommon = ({
    enterDelay,
    exitDelay,
    enterDuration,
    exitDuration,
    notCancelExit,
}) => ({
    exit: {
        y: '100vh',
        delay: exitDelay,
        transition: {
            y: {
                ease: 'circIn',
                duration: exitDuration,
            },
            ...cancelFallDownAnimation(!notCancelExit),
        },
    },
    enter: {
        y: 0,
        delay: props => (props.startedOnce ? 0 : enterDelay),
        transition: props => ({
            y: {
                ease: 'easeOut',
                duration: props.startedOnce ? 0 : enterDuration,
            },
        }),
    },
});

function cancelFallDownAnimation(cancel) {
    if (!cancel) return {};

    return {
        y: {type: 'keyframes', values: [0, 0], times: [0, 1]},
    };
}

const appearFromDown = enterDelay =>
    appearFromDownCommon({
        enterDelay,
        enterDuration: 600,
        exitDuration: 600,
    });

const SmallHeader = posed.div(
    combine(appearFromDown(0), {
        enter: {
            opacity: 1,
            transition: {
                opacity: {duration: 0},
            },
        },
        exit: {
            opacity: 0.2,
            transition: {
                opacity: {ease: 'easeOut', duration: 525},
            },
        },
    })
);

const HeaderBox = posed.header(
    combine(appearFromDown(300), {
        enter: {
            opacity: 1,
        },
        exit: {
            opacity: 1,
            transition: {
                opacity: {ease: 'easeOut', duration: 525},
            },
        },
    })
);

const Header = posed.header({
    enter: {
        y: '2.4rem',
        fontSize: '5em',
        opacity: 1,
        transition: {
            y: {duration: 0},
            fontSize: {duration: 0},
            opacity: {duration: 0},
        },
    },
    exit: {
        y: 0,
        opacity: 0.2,
        fontSize: '1.78em',
        transition: {
            y: {ease: 'easeOut', duration: 525},
            fontSize: {ease: 'easeOut', duration: 525},
            opacity: {ease: 'easeOut', duration: 525},
        },
    },
});

const Content = posed.div({
    exit: {
        x: '-50vw',
        transition: {
            x: {ease: 'easeIn', duration: 400},
        },
    },
    enter: {
        x: 0,
        transition: {
            x: {duration: 0},
        },
    },
});
const IntroDescription = posed.div(appearFromDown(600));
const IntroDescriptionList = posed.div(appearFromDown(600));
const NextButton = posed.div(
    combine(appearFromDown(3600), {
        enter: {
            opacity: 1,
            transition: {duration: 0},
        },
        exit: {
            opacity: 0,
            transition: {duration: 500},
        },
    })
);

const Icon1 = posed.div(
    appearFromDownCommon({
        enterDelay: 1500,
        exitDelay: 400,
        enterDuration: 600,
        exitDuration: 600,
        notCancelExit: true,
    })
);
const Icon2 = posed.div(
    appearFromDownCommon({
        enterDelay: 1700,
        exitDelay: 200,
        enterDuration: 600,
        exitDuration: 600,
        notCancelExit: true,
    })
);
const Icon3 = posed.div(
    appearFromDownCommon({
        enterDelay: 1900,
        exitDelay: 0,
        enterDuration: 600,
        exitDuration: 600,
        notCancelExit: true,
    })
);

const appearByRotateCommon = ({
    enterDelay,
    exitDelay,
    duration,
    enterEase,
    exitEase,
}) => ({
    enter: {
        rotate: 0,
        delay: enterDelay,
        transition: {
            rotate: {ease: enterEase, duration},
        },
    },
    exit: {
        rotate: 90,
        delay: exitDelay,
        transition: {
            rotate: {ease: exitEase, duration},
        },
    },
});

const AnimationBox = posed.div();

const StepBackground = posed.div(
    appearByRotateCommon({
        enterDelay: 900,
        duration: 450,
        enterEase: 'easeOut',
        exitEase: 'easeIn',
    })
);
const StepImage = posed.div(
    appearByRotateCommon({
        enterDelay: 900,
        duration: 500,
        enterEase: 'backOut',
        exitEase: 'backIn',
    })
);

const introSoundWaveProvider1 = makeIntroSoundWaveProvider();
const introSoundWaveProvider2 = makeIntroSoundWaveProvider();

export default class IntroScreen extends Component {
    static propTypes = {
        game: PropTypes.object.isRequired,
        onChangeGame: PropTypes.func.isRequired,
    };

    state = {
        introStep: this.props.game.startedOnce ? 4 : gameConstants.startIntroStep,
        start: false,
    };

    componentDidUpdate() {
        if (this.state.start) {
            this.props.onChangeGame({
                ...createGame(this.props.game),
            });
        }
    }

    render() {
        const {game} = this.props;
        const {startedOnce} = game;
        const {introStep} = this.state;

        const buttonLabel = introStep === 4 ? 'Start' : 'Next';

        return (
            <Section className={styles.introScreen} startedOnce={startedOnce}>
                <div className={styles.paper}>
                    <div className={styles.paperMargin} />
                    <SmallHeader className={styles.smallHeader} startedOnce={startedOnce}>
                        Quest 2:
                    </SmallHeader>
                    <SmallHeader className={styles.smallHeader} startedOnce={startedOnce}>
                        We Got Game
                    </SmallHeader>
                    <HeaderBox className={styles.header} startedOnce={startedOnce}>
                        <Header>Perpetrating Rhymes</Header>
                    </HeaderBox>

                    <div className={styles.headerSpacer} />

                    <Content className={styles.introContent} startedOnce={startedOnce}>
                        <IntroDescription
                            className={styles.introDescription}
                            startedOnce={startedOnce}
                        >
                            Popular music often communicates questionable beliefs about
                            fighting, firearms, and non-violent resolution.
                        </IntroDescription>

                        <IntroDescriptionList className={styles.introDescriptionList}>
                            <StepDescription
                                visible={introStep >= 1}
                                startedOnce={startedOnce}
                            >
                                Listen to an excerpt
                            </StepDescription>
                            <StepDescription
                                visible={introStep >= 2}
                                startedOnce={startedOnce}
                            >
                                Teams take turns to guess
                            </StepDescription>
                            <StepDescription
                                visible={introStep >= 3}
                                startedOnce={startedOnce}
                            >
                                Winning team gets 10 points
                            </StepDescription>
                            <StepDescription
                                visible={introStep >= 4}
                                startedOnce={startedOnce}
                            >
                                Analyze and decode the message!
                            </StepDescription>
                        </IntroDescriptionList>
                    </Content>

                    <div className={styles.buttonSpacer} />

                    <NextButton className={styles.nextButton} startedOnce={startedOnce}>
                        <Button label={buttonLabel} onClick={this.makeStep} />
                    </NextButton>

                    <div className={styles.rightPane}>
                        <PoseGroup flipMove={false}>
                            {introStep === 0 && [
                                <AnimationBox key='step0'>
                                    <Icon1 className={styles.greenCircleIcon} />
                                    <Icon2 className={styles.youtubeIcon} />
                                    <Icon3 className={styles.pIcon} />
                                </AnimationBox>,
                            ]}
                            {introStep === 1 && [
                                <AnimationBox key='step1'>
                                    <StepBackground className={styles.stepBackground} />
                                    <StepImage className={styles.stepImageContainer}>
                                        <SoundWave
                                            className={styles.soundWave}
                                            dataProvider={introSoundWaveProvider1}
                                            stroke='#0A6B40'
                                            opacity={0.2}
                                        />
                                        <div className={styles.step1Image} />
                                    </StepImage>
                                </AnimationBox>,
                            ]}
                            {introStep === 2 && [
                                <AnimationBox key='step2'>
                                    <StepBackground className={styles.stepBackground} />
                                    <StepImage className={styles.stepImageContainer}>
                                        <SoundWave
                                            className={styles.soundWave}
                                            dataProvider={introSoundWaveProvider2}
                                            stroke='#0A6B40'
                                            opacity={0.2}
                                        />
                                        <div className={styles.step2Image} />
                                    </StepImage>
                                </AnimationBox>,
                            ]}
                            {introStep === 3 && [
                                <AnimationBox key='step3'>
                                    <StepBackground className={styles.stepBackground} />
                                    <StepImage className={styles.stepImageContainer}>
                                        <div className={styles.step3Image} />
                                    </StepImage>
                                    <Step3Nimbus className={styles.nimbus} />
                                    <Step3Score className={styles.score} />
                                </AnimationBox>,
                            ]}
                            {introStep === 4 && [
                                <AnimationBox key='step4'>
                                    <StepBackground className={styles.stepBackground} />
                                    <StepImage className={styles.stepImageContainer}>
                                        <div className={styles.step4Image} />
                                    </StepImage>
                                    <Step4CloudText className={styles.cloud} />
                                </AnimationBox>,
                            ]}
                        </PoseGroup>
                    </div>
                    <div className={styles.paperMargin} />
                </div>
            </Section>
        );
    }

    makeStep = () => {
        audioBundle.resume();

        const needStartGame = this.state.introStep === 4;
        const soundKey = needStartGame ? 'startGame' : 'intro';
        audioBundle.play(soundKey);

        this.setState(state => ({
            introStep: Math.min(4, state.introStep + 1),
            start: state.introStep === 4,
        }));
    };
}
